// The Oracle's reading on the board, host-first.
//
// Two halves, two moments: the prediction is called on the initiative
// roll modal (the local die stays shut until it is), the omen is kept at
// setup, where the Dragonlord and Harbinger ceremonies run. The server
// stages a durable modal pendingChoice (promptKind 'oracle') for each
// Oracle seat at SET_FIRST_PLAYER; `foldOraclePromptFromMirror` turns it
// into the overlay for the chooser and the seal answers it. The reading
// itself (omens, prices, Foresight) is REST state on the player row; the
// prompt only holds the start.
//
// Host contract: props (isSpectating, replayDriver, joinRoomCode), state
// (roomCode, oracleReading, oraclePrompt, oracleBusy, oracleError),
// setState, _localSeat, _sendMatchIntent.
import { getSkillState } from '../../../utils/arena/skills';
import { hasReading, canPredict, fetchReading, rerollReading, commitReading } from '../../../utils/arena/oracle';
import { chooseIntent } from '../../../utils/game/net/automationIntents';
import { playUI, UI } from '../../../utils/arena/uiSounds';

const roomCodeOf = (host) => host.state.roomCode || host.props.joinRoomCode || null;

// `state.oracleReading`: undefined until asked, null when this player takes
// no reading here (not an Oracle, spectating, a room that pays nothing),
// else the server view { foresight, maxForesight, pending, rerollCost }.
export async function loadOracleReading(host) {
  if (host.state.oracleReading !== undefined || host._oracleLoading) return;
  if (host.props.isSpectating || host.props.replayDriver || !hasReading(getSkillState())) {
    host.setState({ oracleReading: null });
    return;
  }
  const roomCode = roomCodeOf(host);
  if (!roomCode) return;
  host._oracleLoading = true;
  try {
    const view = await fetchReading(roomCode);
    // A view that landed meanwhile (a reconnect fold) wins over this one.
    if (host._unmounted || host.state.oracleReading !== undefined) return;
    host.setState({ oracleReading: view.unavailable ? null : view });
  } catch (err) {
    console.warn('[oracle] reading failed:', err?.message || err);
    if (!host._unmounted) host.setState({ oracleReading: null });
  } finally {
    host._oracleLoading = false;
  }
}

// What the initiative modal needs to ask for the prediction, or null.
export function predictionProps(host) {
  const reading = host.state.oracleReading;
  if (!canPredict(getSkillState())) return null;
  // Not answered yet: hold the roll-off on the call step with the plates
  // shut, so a quick hand cannot roll before the reading lands.
  if (reading === undefined) return { value: null, busy: true, onPick: () => {} };
  if (!reading) return null;
  return {
    value: reading.pending?.prediction || null,
    busy: !!host.state.oracleBusy,
    onPick: (value) => pickPrediction(host, value),
  };
}

export async function pickPrediction(host, value) {
  const reading = host.state.oracleReading;
  if (!reading?.pending || reading.pending.prediction || host.state.oracleBusy) return;
  host.setState({ oracleBusy: true, oracleError: null });
  try {
    const view = await commitReading(reading.pending.roomCode, null, value);
    playUI(UI.CONFIRM, { volume: 0.6 });
    host.setState({ oracleReading: view, oracleBusy: false });
  } catch (err) {
    host.setState({ oracleBusy: false, oracleError: err?.message || null });
  }
}

export async function rerollOmens(host) {
  const reading = host.state.oracleReading;
  if (!reading?.pending || host.state.oracleBusy) return;
  host.setState({ oracleBusy: true, oracleError: null });
  try {
    const view = await rerollReading(reading.pending.roomCode);
    playUI(UI.CANCEL, { volume: 0.5 });
    host.setState({ oracleReading: view, oracleBusy: false });
  } catch (err) {
    host.setState({ oracleBusy: false, oracleError: err?.message || null });
  }
}

// Answer the setup prompt without asking: 'sealed' when the reading already
// carries an omen (a reconnect), null when this seat takes no reading here.
export function answerPrompt(host, value) {
  const prompt = host.state.oraclePrompt;
  if (prompt?.promptId) host._sendMatchIntent?.(chooseIntent(prompt.promptId, value, prompt.seat));
  host.setState({ oraclePrompt: null });
}

// Keep the omen on the server, then let the start director move on.
export async function sealOmen(host, omenId) {
  const reading = host.state.oracleReading;
  if (!reading?.pending || host.state.oracleBusy) return;
  host.setState({ oracleBusy: true, oracleError: null });
  try {
    const view = await commitReading(reading.pending.roomCode, omenId, null);
    playUI(UI.CONFIRM, { volume: 0.7 });
    host.setState({ oracleReading: view, oracleBusy: false });
    answerPrompt(host, 'sealed');
  } catch (err) {
    host.setState({ oracleBusy: false, oracleError: err?.message || null });
  }
}

// Fold the server's setup prompt. The chooser gets the overlay; the other
// seat just waits. A reading already sealed (a reconnect) answers at once.
// Returns true while the prompt is an oracle prompt.
export function foldOraclePromptFromMirror(host, pc) {
  const isOracle = !!pc && pc.promptKind === 'oracle';
  if (!isOracle) {
    if (host.state.oraclePrompt) host.setState({ oraclePrompt: null });
    return false;
  }
  if (!host.props?.isCasualSandbox && pc.seat !== host._localSeat?.()) return true;
  if (host.state.oraclePrompt?.promptId === pc.promptId) return true;
  host.setState({ oraclePrompt: { promptId: pc.promptId, seat: pc.seat } });
  loadOracleReading(host);
  return true;
}
